// Read the body of a failed fetch response without ever throwing.
//
// Our Cloud Functions answer errors in JSON, so it is tempting to call
// res.json() straight away. The platform underneath them does not play along:
// a function that times out, exhausts its memory, or crashes never reaches our
// error handler, and Google returns a plain-text "Internal Server Error"
// instead. res.json() on that throws a SyntaxError, which then gets reported
// to the user in place of the real problem, hiding an outage behind a parser
// complaint.
//
// Always returns an object. When the body is JSON it is handed back as-is, so
// callers can still branch on flags like `resetSync`. Otherwise the status
// code and a trimmed snippet of the body come back under `error`, which is
// what makes a 500 recognisable as a server failure rather than a bad request.
const MAX_SNIPPET = 160;

export async function readErrorBody(res) {
  const status = res?.status ?? "unknown";
  try {
    return await res.json();
  } catch {
    // Not JSON. Fall back to the raw text, which is where the platform puts
    // its own failures.
    let body = "";
    try {
      body = String((await res.text()) || "").trim();
    } catch {
      body = "";
    }
    const snippet =
      body.length > MAX_SNIPPET ? `${body.slice(0, MAX_SNIPPET)}...` : body;
    return {
      error: snippet
        ? `Server error ${status}: ${snippet}`
        : `Server error ${status}`,
    };
  }
}
